import app from 'flarum/forum/app';
import StoryViewer from './components/StoryViewer';
import store from './storyStore';

/**
 * Open one user's stories in a full-screen viewer.
 *
 * Rings live on avatars scattered across the page, so the viewer can't be a
 * child of whatever component drew the avatar — it is mounted onto its own
 * node under <body> and torn down on close.
 */
let mountPoint = null;
let busy = false;

/**
 * @param {object} user     the author, as a Flarum User model
 * @param {number} [storyId] start at this story instead of the first; when it
 *                           has expired or been deleted, say so instead
 */
export default function openUserStories(user, storyId) {
  if (busy || mountPoint) return;

  busy = true;

  store
    .loadUser(user.id())
    .then((groups) => {
      busy = false;

      // The story may have expired between page load and this click.
      if (!groups.length) {
        store.unmarkAuthor(user.id());
        if (storyId) showExpired();
        m.redraw();
        return;
      }

      let startStory = 0;

      if (storyId) {
        startStory = groups[0].stories.findIndex((s) => Number(s.id) === Number(storyId));

        // Asked for one story in particular, and it is gone: opening the
        // others instead would look like the link went to the wrong place.
        if (startStory === -1) {
          showExpired();
          m.redraw();
          return;
        }
      }

      mount(groups, startStory);
    })
    .catch(() => {
      busy = false;
    });
}

function showExpired() {
  app.alerts.show({ type: 'error' }, app.translator.trans('happydigital-stories.forum.story_expired'));
}

function mount(groups, startStory = 0) {
  mountPoint = document.createElement('div');
  mountPoint.className = 'StoryViewer-mount';
  document.body.appendChild(mountPoint);

  m.mount(mountPoint, {
    view: () =>
      StoryViewer.component({
        groups,
        startGroup: 0,
        startStory,
        onclose: unmount,
        onliketoggle: (g, i, result) => {
          const story = storyAt(groups, g, i);
          if (!story) return;
          story.isLiked = result.isLiked;
          // Owner-only — see StoryBar.handleLikeToggle.
          if (result.likesCount !== undefined) story.likesCount = result.likesCount;
          story.myReaction = result.myReaction;
          // Absent for anyone but the owner — see StoryBar.handleLikeToggle.
          if (result.reactions) story.reactions = result.reactions;
        },
        onviewed: (g, i, result) => {
          const story = storyAt(groups, g, i);
          if (!story) return;

          story.isViewed = true;
          story.viewsCount = result.viewsCount;

          // The ring only dims once every story of this user has been seen.
          const group = groups[g];
          if (group && group.stories.every((s) => s.isViewed)) {
            group.isViewed = true;
            store.markSeen(group.user.id);

            // Rings and badges live in the main tree, which this viewer is
            // mounted outside of; without an explicit redraw they keep their
            // old state until something else happens to repaint them.
            redrawSoon();
          }
        },
        ondelete: (g, i) => {
          const group = groups[g];
          if (!group) return;

          group.stories.splice(i, 1);

          if (group.stories.length === 0) {
            // The ring should disappear too.
            store.unmarkAuthor(group.user.id);
            unmount();
          }
        },
      }),
  });
}

function unmount() {
  if (!mountPoint) return;

  m.mount(mountPoint, null);
  mountPoint.remove();
  mountPoint = null;

  // Rings elsewhere on the page may have changed state. Deferred because a
  // redraw issued while the mount point is being torn down can miss the main
  // tree entirely.
  redrawSoon();
}

/**
 * Redraw on the next tick.
 *
 * The viewer runs in its own mount root, so state it changes has to be pushed
 * to the main tree explicitly — and a redraw requested mid-teardown or during
 * another redraw is dropped.
 */
function redrawSoon() {
  setTimeout(() => m.redraw(), 0);
}

function storyAt(groups, groupIdx, storyIdx) {
  const group = groups[groupIdx];

  return group && group.stories[storyIdx];
}
